import { Box, CircularProgress } from '@mui/material';
import React from 'react';
import { KitButton, KitCard, KitList, KitPill } from '../common/MobileKit';
import { KitInput } from '../common/MobileKitExtras';
import { KitEmptyNote, Legend } from '../common/MobileDashboardKit';
import { MobileListRow } from '../common/MobileCrud';
import { protoIcon } from '../common/ProtoIcon';
import { ListBlock, MiniStat, PagedList } from './MobileReportLists';

/**
 * Peças do Relatório de Produtos no shell mobile (rentabilidade com custo editável, gráfico de
 * linhas, listas "Relevantes" e "Abaixo da margem"). Só apresentação: dados, cálculos, permissões
 * e chamadas vêm de `pages/reports/ProductReport.js`.
 */

const CheckIcon = protoIcon('check');

// Linha de "Rentabilidade". `getRow(row)` devolve o modelo de exibição já calculado pela tela:
// { name, isSale, price, qty, cost, costEditable, draft, dirty, invalid, saving, profit, profitNegative,
//   profitPreview, margin, marginNegative, marginPreview }
export function ProfitabilityList({ rows, getRow, onDraftChange, onSave, pageSize = 20 }) {
    return (
        <PagedList
            rows={rows}
            pageSize={pageSize}
            noun="linhas"
            empty="Nenhum produto vendido no período selecionado."
            renderRow={(row) => {
                const r = getRow(row);
                return (
                    <ListBlock>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Box sx={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: 14, lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name || '—'}</Box>
                            <KitPill tone={r.isSale ? 'info' : 'purple'}>{r.isSale ? 'Venda' : 'O.S.'}</KitPill>
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '10px', mt: '8px' }}>
                            <MiniStat label="Preço de venda" value={r.price} />
                            <MiniStat label="Qtd. vendida" value={r.qty} />
                        </Box>
                        {r.costEditable ? (
                            <Box sx={{ mt: '10px' }}>
                                <Box sx={{ fontSize: 11, color: 'text.disabled', lineHeight: 1.4, mb: '4px' }}>Custo (R$)</Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <KitInput
                                        inputMode="numeric"
                                        aria-label={`Custo de ${r.name}`}
                                        value={r.draft}
                                        error={r.invalid}
                                        disabled={r.saving}
                                        onChange={(e) => onDraftChange(row, e.target.value)}
                                        sx={{ height: 44, flex: 1, minWidth: 0 }}
                                    />
                                    {r.dirty && (
                                        <KitButton
                                            variant={r.invalid ? 'danger' : 'success'}
                                            aria-label={r.invalid ? 'Custo não pode ser maior que o preço de venda' : 'Salvar novo custo'}
                                            disabled={r.invalid || r.saving}
                                            onClick={() => onSave(row)}
                                            sx={{ width: 44, minWidth: 44, p: 0, height: 44, minHeight: 44 }}
                                        >
                                            {r.saving ? <CircularProgress size={18} color="inherit" /> : <CheckIcon size={20} />}
                                        </KitButton>
                                    )}
                                </Box>
                                {r.invalid && (
                                    <Box sx={{ fontSize: 12, color: 'error.main', mt: '4px', lineHeight: 1.4 }}>Custo não pode ser maior que o preço de venda</Box>
                                )}
                            </Box>
                        ) : (
                            <Box sx={{ mt: '8px' }}><MiniStat label="Custo" value={r.cost} /></Box>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', mt: '10px' }}>
                            <Box sx={{ minWidth: 0 }}>
                                <Box sx={{ fontSize: 11, color: 'text.disabled', lineHeight: 1.4 }}>Lucro</Box>
                                <Box sx={{ fontSize: 14, fontWeight: 800, lineHeight: 1.4, fontStyle: r.profitPreview ? 'italic' : 'normal', color: r.profitNegative ? 'error.main' : 'success.main' }}>{r.profit}</Box>
                            </Box>
                            <KitPill tone={r.marginNegative ? 'danger' : 'success'}>{r.margin}{r.marginPreview ? ' (prévia)' : ''}</KitPill>
                        </Box>
                    </ListBlock>
                );
            }}
        />
    );
}

// Linhas de "Receita x Custo x Líquido": polilinhas sobre a mesma escala (o líquido pode ser negativo).
// `items`: [{ label, series: [receita, custo, liquido] }]; `colors`: uma cor por série.
export function MultiLine({ items, colors, maxLabels = 6 }) {
    const n = items.length;
    const all = items.flatMap((i) => i.series);
    const max = Math.max(1, ...all);
    const min = Math.min(0, ...all);
    const span = max - min || 1;
    const xs = (i) => (n === 1 ? 50 : (i / (n - 1)) * 100);
    const ys = (v) => 96 - ((v - min) / span) * 92;
    const step = Math.max(1, Math.ceil(n / maxLabels));
    return (
        <Box>
            <Box sx={{ position: 'relative', height: 130 }}>
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', pointerEvents: 'none' }}>
                    {[0, 1, 2].map((k) => <Box key={k} sx={{ borderTop: '1px solid', borderColor: 'divider' }} />)}
                </Box>
                <Box component="svg" viewBox="0 0 100 100" preserveAspectRatio="none" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}>
                    {colors.map((color, s) => (
                        <polyline
                            key={color}
                            points={items.map((it, i) => `${xs(i)},${ys(it.series[s])}`).join(' ')}
                            fill="none"
                            stroke={color}
                            strokeWidth="2"
                            strokeLinejoin="round"
                            strokeLinecap="round"
                            vectorEffect="non-scaling-stroke"
                        />
                    ))}
                </Box>
            </Box>
            <Box sx={{ position: 'relative', height: 18, mt: '6px' }}>
                {items.map((it, i) => (i % step === 0 ? (
                    <Box
                        key={`${it.label}-${i}`}
                        component="span"
                        sx={{
                            position: 'absolute',
                            left: `${xs(i)}%`,
                            transform: i === 0 && n > 1 ? 'none' : i === n - 1 && n > 1 ? 'translateX(-100%)' : 'translateX(-50%)',
                            fontSize: 10.5,
                            color: 'text.disabled',
                            fontWeight: 600,
                            lineHeight: 1.4,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {it.label}
                    </Box>
                ) : null))}
            </Box>
        </Box>
    );
}

export function SeriesKey({ items }) {
    return (
        <Box sx={{ display: 'flex', gap: '16px', mt: '10px', flexWrap: 'wrap' }}>
            <Legend items={items} />
        </Box>
    );
}

// Lista simples (nome + valor/pill à direita) das abas "Relevantes".
export function SimpleRows({ rows, empty }) {
    if (rows.length === 0) return <KitCard><KitEmptyNote>{empty}</KitEmptyNote></KitCard>;
    return (
        <KitList>
            {rows.map((row, index) => (
                <MobileListRow key={`${row.title}-${index}`} title={row.title} end={row.end} />
            ))}
        </KitList>
    );
}

// Linha de "Abaixo da margem". `getRow(row)`: { name, salePrice, current, expected, suggested }.
export function BelowMarginList({ rows, getRow, loading, canApply, onApply, pageSize = 20 }) {
    if (loading) return <KitCard><KitEmptyNote>Carregando...</KitEmptyNote></KitCard>;
    return (
        <PagedList
            rows={rows}
            pageSize={pageSize}
            noun="produtos"
            empty="Nenhum produto abaixo da margem esperada no momento."
            renderRow={(row) => {
                const r = getRow(row);
                return (
                    <ListBlock>
                        <Box sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name || '—'}</Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '10px', mt: '8px' }}>
                            <MiniStat label="Valor de venda" value={r.salePrice} />
                            <MiniStat label="Preço sugerido" value={r.suggested} strong />
                        </Box>
                        <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', mt: '10px' }}>
                            <KitPill tone="danger">Atual {r.current}</KitPill>
                            <KitPill tone="info">Esperada {r.expected}</KitPill>
                        </Box>
                        {canApply && (
                            <KitButton variant="soft" block sx={{ mt: '10px' }} onClick={() => onApply(row)}>
                                Aplicar preço sugerido
                            </KitButton>
                        )}
                    </ListBlock>
                );
            }}
        />
    );
}
